import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { ExclamationDiamondFill } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { OptionsType, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';

import { pathnames, requestItems } from '../../api';
import { getAxiosErrorResponse } from '../../api/utils';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import MeasurementDeviceItem, {
  MeasurementDeviceItemArrayPropType,
} from '../../models/data/MeasurementDeviceItem';
import { toItemIdentifierQueryParam } from '../../models/requests/ItemIdentifier';

const FormatOptionLabel = (item: MeasurementDeviceItem) => (
  <div className="d-flex">
    <div className="d-block">
      <small className="m-0 text-secondary d-block">
        {toItemIdentifierQueryParam(item, '+')}
      </small>
      <div>
        <span className="m-0">{item.itemName ?? ''}</span>
        <small className="m-0 text-secondary ml-1">{item.unit}</small>
      </div>
    </div>
  </div>
);

export interface SelectItemsProps {
  className?: string;
  isDisabled?: boolean;
  customerEquipmentId?: number;
  selectedMeasurementDeviceItems?: MeasurementDeviceItem[];
  onChange?: (items: MeasurementDeviceItem[]) => void;
}

const SelectItems: React.FunctionComponent<SelectItemsProps> = ({
  className,
  isDisabled,
  customerEquipmentId,
  selectedMeasurementDeviceItems,
  onChange,
}) => {
  const context = useContext(AuthenticatedContext);
  // ---------------------------------------------------------------------
  // Create WEB API URL.
  const history = useHistory();
  const url = history.createHref({ pathname: pathnames.items });
  // ---------------------------------------------------------------------
  // Status
  const [cacheVersion, setCacheVersion] = useState(1);
  const [defaultOptions, setDefaultOptions] = useState<
    OptionsType<MeasurementDeviceItem>
  >([]);
  const [selectedOption, setSelectedOption] = useState<
    ValueType<MeasurementDeviceItem, true>
  >(selectedMeasurementDeviceItems);
  // ---------------------------------------------------------------------
  // Callbacks & Functions
  const onChangeSelect = (option: ValueType<MeasurementDeviceItem, true>) => {
    setSelectedOption(option);
    if (onChange != null) {
      if (option != null) {
        onChange([...option]);
      } else {
        onChange([]);
      }
    }
  };
  const toastError = (error: unknown) => {
    const res = getAxiosErrorResponse(error);
    toast.error(
      () => {
        return (
          <>
            <ExclamationDiamondFill />
            <span>{` 測定項目 取得失敗`}</span>
            <small>{` (${res?.status ?? '応答なし'})`}</small>
          </>
        );
      },
      {
        onClose: () => {
          if (res != null && res.status >= 400 && res.status < 500) {
            // 認証エラーの可能性大、ログインチェック（期限切れの場合ログイン画面へ遷移する。はず）
            context.login().catch(() => {
              // do nothing.
            });
          }
        },
      },
    );
  };
  const loadOptions = (
    name: string,
  ): Promise<OptionsType<MeasurementDeviceItem>> => {
    if (customerEquipmentId == null) {
      return Promise.resolve([]);
    }
    return requestItems(url, {
      name: name === '' ? undefined : name,
      eqid: customerEquipmentId,
    })
      .then(({ data }) => {
        return data;
      })
      .catch((error) => {
        toastError(error);
        throw error;
        // return [];
      });
  };
  // ---------------------------------------------------------------------
  // Effects
  useEffect(() => {
    setSelectedOption(selectedMeasurementDeviceItems);
  }, [selectedMeasurementDeviceItems]);
  //  --- Only re-subscribe if props.isDisabled or props.customerEquipmentId changes
  useEffect(() => {
    let unmounted = false;
    setCacheVersion(cacheVersion + 1); // Purge cache request.
    loadOptions('')
      .then((items) => {
        if (!unmounted) {
          setDefaultOptions(items);
        }
      })
      .catch(() => {
        if (!unmounted) {
          setDefaultOptions([]);
          setSelectedOption([]);
        }
      });
    if (selectedMeasurementDeviceItems != null) {
      setSelectedOption(
        selectedMeasurementDeviceItems.filter((i) => {
          return i.customerEquipmentId === customerEquipmentId;
        }),
      );
    }
    return () => {
      unmounted = true;
    };
  }, [isDisabled, customerEquipmentId]);
  // ---------------------------------------------------------------------
  // Components
  return (
    <AsyncSelect
      className={className}
      placeholder="計測項目を選択してください"
      isDisabled={isDisabled}
      defaultOptions={defaultOptions}
      formatOptionLabel={FormatOptionLabel}
      cacheOptions={cacheVersion}
      loadOptions={loadOptions}
      value={selectedOption}
      onChange={onChangeSelect}
      getOptionLabel={(opt) => opt.itemName ?? ''}
      getOptionValue={(item) => {
        return toItemIdentifierQueryParam(item, '+');
      }}
      isMulti
      styles={{
        // fix #1085
        menu: (zzz) => {
          return {
            ...zzz,
            zIndex: 9999,
          };
        },
      }}
    />
  );
};

SelectItems.propTypes = {
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  customerEquipmentId: PropTypes.number,
  selectedMeasurementDeviceItems: MeasurementDeviceItemArrayPropType,
  onChange: PropTypes.func,
};

SelectItems.defaultProps = {
  className: undefined,
  isDisabled: undefined,
  customerEquipmentId: undefined,
  selectedMeasurementDeviceItems: undefined,
  onChange: undefined,
};

export default SelectItems;
